/* eslint-disable max-len */
import React, { useEffect, useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { scaleOrdinal } from 'd3-scale';
import { select } from 'd3-selection';
import { colors } from '../../utils/colorsGrafica';
import { fechaHora } from '../../utils/parsers/date';

// Componente D3 Sankey personalizado
const D3SankeyChart = ({ nodes, links, width = 850, height = 500 }) => {
  const svgRef = useRef();
  const colorScale = scaleOrdinal(colors);

  useEffect(() => {
    if (!nodes || !links || nodes.length === 0) return;

    // Validar que todos los links tengan source y target válidos
    const validLinks = links.filter(link => {
      const sourceValid = link.source !== undefined && link.source !== null && link.source >= 0 && link.source < nodes.length;
      const targetValid = link.target !== undefined && link.target !== null && link.target >= 0 && link.target < nodes.length;
      return sourceValid && targetValid;
    });

    if (validLinks.length === 0) {
      console.warn('No hay links válidos para renderizar el Sankey');
      return;
    }

    const svg = select(svgRef.current);
    svg.selectAll('*').remove();

    const sankeyGenerator = sankey()
      .nodeWidth(15)
      .nodePadding(10)
      .extent([[1, 1], [width - 1, height - 6]]);

    let sankeyData;
    try {
      sankeyData = sankeyGenerator({
        nodes: nodes.map(d => ({ ...d })),
        links: validLinks.map(d => ({ ...d }))
      });
    } catch (error) {
      console.error('Error al generar diagrama Sankey:', error);
      return;
    }

    const { nodes: sankeyNodes, links: sankeyLinks } = sankeyData;

    // Dibujar enlaces
    svg.append('g')
      .selectAll('path')
      .data(sankeyLinks)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', d => colorScale(d.target.index))
      .attr('stroke-width', d => Math.max(1, d.width))
      .attr('fill', 'none')
      .attr('opacity', 0.5);

    // Dibujar nodos
    const nodeGroup = svg.append('g')
      .selectAll('g')
      .data(sankeyNodes)
      .join('g');

    nodeGroup.append('rect')
      .attr('x', d => d.x0)
      .attr('y', d => d.y0)
      .attr('height', d => d.y1 - d.y0)
      .attr('width', d => d.x1 - d.x0)
      .attr('fill', (d, i) => colorScale(i))
      .attr('opacity', 0.8);

    // Agregar etiquetas de nombre
    nodeGroup.append('text')
      .attr('x', d => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
      .attr('y', d => (d.y1 + d.y0) / 2 - 10)
      .attr('dy', '0.35em')
      .attr('text-anchor', d => d.x0 < width / 2 ? 'start' : 'end')
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .attr('fill', '#333')
      .text(d => d.name);

    // Agregar etiquetas de valor
    nodeGroup.append('text')
      .attr('x', d => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
      .attr('y', d => (d.y1 + d.y0) / 2 + 5)
      .attr('dy', '0.35em')
      .attr('text-anchor', d => d.x0 < width / 2 ? 'start' : 'end')
      .attr('font-size', '10px')
      .attr('fill', '#666')
      .text(d => `${d.value?.toLocaleString() || ''} ${d.unit || ''}`);

  }, [nodes, links, width, height, colorScale]);

  return <svg ref={svgRef} width={width} height={height} />;
};

D3SankeyChart.propTypes = {
  nodes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.number,
    unit: PropTypes.string
  })).isRequired,
  links: PropTypes.arrayOf(PropTypes.shape({
    source: PropTypes.number.isRequired,
    target: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  })).isRequired,
  width: PropTypes.number,
  height: PropTypes.number
};

D3SankeyChart.defaultProps = {
  width: 850,
  height: 500
};

const Sankey = forwardRef(({ data, title, isNewFormat = false, loading = false, error = null }, ref) => {
  const [sankeyData, setSankeyData] = useState({ nodes: [], links: [] });
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    let nodes = [];
    let links = [];
    let meta = null;

    // Si es el nuevo formato de datos de la API
    if (isNewFormat && data) {
      if (data.nodes && data.links) {
        // Crear un mapa de IDs a índices numéricos
        const nodeIdMap = new Map();
        nodes = data.nodes.map((node, index) => {
          nodeIdMap.set(node.id, index);
          return {
            id: index,
            name: node.name,
            value: node.value,
            unit: node.unidades || ''
          };
        });
        
        // Convertir links usando los índices numéricos
        links = data.links.map(link => ({
          source: typeof link.source === 'number' ? link.source : nodeIdMap.get(link.source),
          target: typeof link.target === 'number' ? link.target : nodeIdMap.get(link.target),
          value: link.value
        }));
        
        meta = data.metadata;
      }
    } 
    // Formato legacy existente
    else if (data && data.nivel) {
      const nodeMap = new Map();
      const linksArray = [];
      let nodeIndex = 0;

      const getOrCreateNode = (name, value, unit = '') => {
        if (!nodeMap.has(name)) {
          nodeMap.set(name, { 
            id: nodeIndex++, 
            name, 
            value, 
            unit 
          });
        }
        return nodeMap.get(name).id;
      };

      if (data.nivel === 'proyecto') {
        const proyectoId = getOrCreateNode(
          data.graficaSankey.nombre, 
          data.graficaSankey.totalProyecto,
          'KWh'
        );

        data.graficaSankey.elementos.forEach(elemento => {
          const elementoId = getOrCreateNode(
            `E. ${elemento.nombre}`, 
            elemento.totalElemento,
            'KWh'
          );
          linksArray.push({ 
            source: proyectoId, 
            target: elementoId, 
            value: elemento.totalElemento 
          });

          elemento.tarjetas.forEach(tarjeta => {
            const tarjetaId = getOrCreateNode(
              `T. ${tarjeta.nombre}`, 
              tarjeta.totalTarjeta,
              'KWh'
            );
            linksArray.push({ 
              source: elementoId, 
              target: tarjetaId, 
              value: tarjeta.totalTarjeta 
            });

            tarjeta.sensores.forEach(sensor => {
              const sensorValue = Number(sensor.data[0]?.total?.$numberDecimal);
              const sensorId = getOrCreateNode(
                sensor.nombre, 
                sensorValue,
                'KWh'
              );
              linksArray.push({ 
                source: tarjetaId, 
                target: sensorId, 
                value: sensorValue 
              });
            });
          });
        });
      }

      if (data.nivel === 'elemento') {
        const elementoId = getOrCreateNode(
          data.graficaSankey.nombre, 
          data.graficaSankey.totalElemento,
          'KWh'
        );

        data.graficaSankey.tarjetas.forEach(tarjeta => {
          const tarjetaId = getOrCreateNode(
            `T. ${tarjeta.nombre}`, 
            tarjeta.totalTarjeta,
            'KWh'
          );
          linksArray.push({ 
            source: elementoId, 
            target: tarjetaId, 
            value: tarjeta.totalTarjeta 
          });

          tarjeta.sensores.forEach(sensor => {
            const sensorValue = Number(sensor.data[0]?.total?.$numberDecimal);
            const sensorId = getOrCreateNode(
              sensor.nombre, 
              sensorValue,
              'KWh'
            );
            linksArray.push({ 
              source: tarjetaId, 
              target: sensorId, 
              value: sensorValue 
            });
          });
        });
      }

      nodes = Array.from(nodeMap.values());
      links = linksArray;
    }

    setSankeyData({ nodes, links });
    setMetadata(meta);
  }, [data, isNewFormat]);

  if (loading) {
    return (
      <div className="card-grafica-container">
        <div className="card-grafica-container__header">
          <p className="card-grafica-container__title">{title}</p>
        </div>
        <div className="card-grafica-container__body-grafica-sankey">
          <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Cargando gráfica Sankey...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-grafica-container">
        <div className="card-grafica-container__header">
          <p className="card-grafica-container__title">{title}</p>
        </div>
        <div className="card-grafica-container__body-grafica-sankey">
          <div className="error-container" style={{ textAlign: 'center', padding: '2rem', color: '#e74c3c' }}>
            <p>Error al cargar la gráfica: {error.message || 'Error desconocido'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-grafica-container">
      <div className="card-grafica-container__header">
        <p className="card-grafica-container__title">{title}</p>
      </div>
      
      {/* Mostrar información de fechas si está disponible (formato legacy) */}
      {data?.fechaInicio && data?.fechaFin && (
        <div>
          <p className="text-reporte-2">
            {`(${fechaHora(data.fechaInicio)} - ${fechaHora(data.fechaFin)})`}
          </p>
        </div>
      )}

      {/* Mostrar metadatos si están disponibles (nuevo formato) */}
      {metadata && (
        <div className="sankey-metadata" style={{ padding: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
          {metadata.fechaConsulta && (
            <p>Fecha de consulta: {fechaHora(metadata.fechaConsulta)}</p>
          )}
          {metadata.totalRegistros && (
            <p>Registros consultados: {metadata.totalRegistros}</p>
          )}
          {metadata.unidadesPrincipales && metadata.unidadesPrincipales.length > 0 && (
            <p>Unidades: {metadata.unidadesPrincipales.join(', ')}</p>
          )}
          {metadata.sensoresCount && (
            <p>Total de sensores: {metadata.sensoresCount}</p>
          )}
        </div>
      )}

      <div className="card-grafica-container__body-grafica-sankey">
        {sankeyData?.nodes?.length > 0 ? (
          <div ref={ref} style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <D3SankeyChart 
              nodes={sankeyData.nodes} 
              links={sankeyData.links}
              width={850}
              height={500}
            />
          </div>
        ) : (
          <p className="text-nothing">
            Los sensores de la gráfica
            {' '}
            {title}
            {' '}
            no contienen datos
          </p>
        )}
      </div>
    </div>
  );
});

Sankey.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({
      // Formato legacy
      nivel: PropTypes.string,
      graficaSankey: PropTypes.shape(),
      fechaInicio: PropTypes.string,
      fechaFin: PropTypes.string
    }),
    PropTypes.shape({
      // Nuevo formato
      nodes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        level: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        originalId: PropTypes.string.isRequired,
        unidades: PropTypes.string,
        tipo: PropTypes.string,
        metadata: PropTypes.shape()
      })),
      links: PropTypes.arrayOf(PropTypes.shape({
        source: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        unidades: PropTypes.string.isRequired
      })),
      metadata: PropTypes.shape({
        totalRegistros: PropTypes.number,
        fechaConsulta: PropTypes.string,
        unidadesPrincipales: PropTypes.arrayOf(PropTypes.string),
        sensoresCount: PropTypes.number
      }),
      chartData: PropTypes.array
    })
  ]),
  title: PropTypes.string.isRequired,
  isNewFormat: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.object
};

Sankey.displayName = 'Sankey';

export default Sankey;